/**
 * search-palette.tsx
 * Presentational command-palette pieces extracted from search-modal.tsx:
 * the row/group types, CREATE_DEFS/NAV_DEFS static config, the flat-row
 * helpers, and the grouped row renderer (SearchPalette). SearchModal owns
 * all state (query, keyboard, scroll container, activate dispatcher) and
 * passes data down here.
 */

import {
  BookOpen, Calendar, CheckCircle, Flame, FolderKanban, Goal,
  Sun, Inbox, BookMarked, Wallet, Plus, Compass,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { EntityKind } from "./entity-form";

// ── Types ─────────────────────────────────────────────────────────────────────

export type Hit = { id: string; view: string; type: string; title: string; detail: string; icon: LucideIcon };

/** A flat selectable row in the palette */
export type PaletteRow =
  | { kind: "entity";   data: Hit }
  | { kind: "create";   entityKind: EntityKind; label: string; icon: LucideIcon; title: string }
  | { kind: "nav";      view: string; label: string; icon: LucideIcon };

export type Group = { heading: string; rows: PaletteRow[] };

// ── Static config ─────────────────────────────────────────────────────────────

export const CREATE_DEFS: Array<{ entityKind: EntityKind; label: string; prefix: string; icon: LucideIcon }> = [
  { entityKind: "task",        label: "Thêm việc",     prefix: "Thêm việc",    icon: CheckCircle },
  { entityKind: "note",        label: "Thêm ghi chú",  prefix: "Thêm ghi chú", icon: BookOpen },
  { entityKind: "goal",        label: "Thêm mục tiêu", prefix: "Thêm mục tiêu",icon: Goal },
  { entityKind: "project",     label: "Thêm dự án",    prefix: "Thêm dự án",   icon: FolderKanban },
  { entityKind: "event",       label: "Thêm lịch",     prefix: "Thêm lịch",    icon: Calendar },
];

export const NAV_DEFS: Array<{ view: string; label: string; icon: LucideIcon }> = [
  { view: "today",    label: "Hôm nay",      icon: Sun },
  { view: "tasks",    label: "Việc cần làm", icon: CheckCircle },
  { view: "projects", label: "Dự án",        icon: FolderKanban },
  { view: "goals",    label: "Mục tiêu",     icon: Goal },
  { view: "habits",   label: "Thói quen",    icon: Flame },
  { view: "calendar", label: "Lịch",         icon: Calendar },
  { view: "notes",    label: "Ghi chú",      icon: BookOpen },
  { view: "finance",  label: "Tài chính",    icon: Wallet },
  { view: "inbox",    label: "Inbox",        icon: Inbox },
  { view: "journal",  label: "Nhật ký",      icon: BookMarked },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

export function flatRows(groups: Group[]): PaletteRow[] {
  return groups.flatMap((g) => g.rows);
}

export function rowKey(row: PaletteRow): string {
  if (row.kind === "entity") return `entity-${row.data.id}`;
  if (row.kind === "create") return `create-${row.entityKind}`;
  return `nav-${row.view}`;
}

// ── SearchPalette (presentational rows) ─────────────────────────────────────────

type SearchPaletteProps = {
  groups: Group[];
  /** Index into the flattened row list of the currently highlighted row */
  activeIdx: number;
  onActivate: (row: PaletteRow) => void;
};

/**
 * Renders the grouped, highlightable rows. Returns a fragment so SearchModal
 * keeps owning the scroll container (and its empty-state messages), preserving
 * the original DOM structure exactly.
 */
export function SearchPalette({ groups, activeIdx, onActivate }: SearchPaletteProps) {
  // Track cumulative flat index across groups for highlight
  let flatCursor = 0;

  return (
    <>
      {groups.map((group) => (
        <div key={group.heading}>
          {/* Group heading */}
          <p className="px-3 pb-1 pt-2.5 text-[11px] font-bold uppercase tracking-widest text-brand-500">
            {group.heading}
          </p>

          {group.rows.map((row) => {
            const idx = flatCursor++;
            const isActive = idx === activeIdx;
            const key = rowKey(row);

            if (row.kind === "entity") {
              return (
                <button
                  key={key}
                  data-active={isActive}
                  onClick={() => onActivate(row)}
                  className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition ${isActive ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-ink-200/40"}`}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-100 text-brand-600">
                    <row.data.icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold">{row.data.title}</p>
                    <p className="truncate text-[12px] text-ink-500">{row.data.type} · {row.data.detail}</p>
                  </div>
                </button>
              );
            }

            if (row.kind === "create") {
              return (
                <button
                  key={key}
                  data-active={isActive}
                  onClick={() => onActivate(row)}
                  className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition ${isActive ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-ink-200/40"}`}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <Plus className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold">{row.title}</p>
                    <p className="truncate text-[12px] text-ink-500">{row.label}</p>
                  </div>
                </button>
              );
            }

            // nav row
            return (
              <button
                key={key}
                data-active={isActive}
                onClick={() => onActivate(row)}
                className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition ${isActive ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-ink-200/40"}`}
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-ink-100 text-ink-600">
                  <Compass className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold">{row.label}</p>
                  <p className="truncate text-[12px] text-ink-500">Đi tới màn hình</p>
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}
